import type { TRunWithPayload } from 'pullstate';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import useMe from '@/renderer/utils/useMe';
import { useInfiniteRequest } from '@/renderer/utils/useRequest2';

const JITTER = 5 * 60 * 1000 * Math.random();
const MANUAL_UPDATE_INTERVAL = 60 * 60 * 1000 + JITTER;

export function useMemberships({
  // If true, merges memberships with the same productId into one by adding remaining and merging bonuses
  mergeSame = false,
  // Temporary for testing in prod
  omitEmpty = false,
} = {}) {
  const me = useMe();

  const infinite = useInfiniteRequest<
    API.Membership<true>,
    API.PageInput,
    API.WithPageInput<
      API.WithData<{ bonus?: 'include' | 'separate' | 'omit'; expired?: 'buyable' | 'omit' }>
    >
  >({
    key: `memberships:${me?.id}`,
    req: `/api/v1/membership/me`,
    event: 'membership',
    pause: !me,
    skipPoll: true,
    query: {
      page: {
        limit: 100,
      },
    },
  });

  const dataRef = useRef(infinite.data);
  const [data, setData] = useState(infinite.data);

  useEffect(() => {
    if (mergeSame || omitEmpty) {
      if (infinite.data?.data) {
        const newData: API.WithPage<API.Membership<true>[], void> = {
          ...infinite.data,
          data: [],
        };

        const ids: Map<string, API.Membership<true>> = new Map();
        const idxs: Map<string, number> = new Map();

        for (const membership of infinite.data.data) {
          if (omitEmpty && membership.remaining === 0) {
            continue;
          }

          const key = `${membership.productId}:${membership.pauseUntil}`;
          const idx = idxs.get(key);
          const old = ids.get(key);

          if (mergeSame && idx !== undefined && old) {
            const next = {
              ...old,
              remaining: old.remaining + membership.remaining,
              bonuses:
                old.bonuses?.length || membership.bonuses?.length
                  ? (old.bonuses || []).concat(membership.bonuses || [])
                  : old.bonuses,
              pauseUntil:
                new Date(old.pauseUntil) > new Date(membership.pauseUntil)
                  ? old.pauseUntil
                  : membership.pauseUntil,
            };

            ids.set(key, next);
            newData.data[idx] = next;
          } else {
            idxs.set(key, newData.data.push(membership) - 1);
            ids.set(key, membership);
          }
        }

        dataRef.current = newData;
        setData(newData);
      } else if (infinite.data !== dataRef.current) {
        dataRef.current = infinite.data; // undefined or null
        setData(infinite.data);
      }
    }
  }, [infinite.data, infinite.data?.data, mergeSame, omitEmpty]);

  const renderPayload: TRunWithPayload<API.WithPage<API.Membership<true>[]>> = useCallback(
    (func) => {
      if (!infinite.error && dataRef.current) {
        return func(dataRef.current);
      }

      return Fragment;
    },
    [data],
  );

  /// @todo polling, and removing memberships when remaining = 0

  if (mergeSame || omitEmpty) {
    return new Proxy<typeof infinite>(infinite, {
      get(target, prop) {
        if (prop === 'data') {
          return dataRef.current;
        } else if (prop === 'renderPayload') {
          return renderPayload;
        }

        return Reflect.get(target, prop);
      },
    });
  }

  return infinite;
}
